import { useRouter } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, Download, FileText, Plus, Search, X } from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCommitments } from "@/hooks/use-commitments";
import { useBillingPeriods, useCreateSubPayApp, useSubPayApps } from "@/hooks/use-pay-applications";
import { useProjects } from "@/hooks/use-projects";

const STATUSES = [
  "All",
  "Draft",
  "Submitted",
  "Under Review",
  "Approved",
  "Rejected",
  "Returned",
  "Paid",
] as const;

type SortCol =
  | "payAppNumber"
  | "projectName"
  | "commitmentNumber"
  | "billingPeriodEnd"
  | "currentPaymentDue"
  | "createdAt"
  | "status";

function statusToFilter(label: string): string | undefined {
  if (label === "All") return undefined;
  if (label === "Returned") return "returned_for_revision";
  return label.toLowerCase().replace(/ /g, "_");
}

function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "--";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDate(date: string | null | undefined): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function statusVariant(
  status: string | null,
): "success" | "warning" | "secondary" | "destructive" | "default" {
  switch (status) {
    case "approved":
    case "paid":
      return "success";
    case "draft":
    case "submitted":
      return "warning";
    case "under_review":
      return "secondary";
    case "rejected":
    case "returned_for_revision":
      return "destructive";
    default:
      return "default";
  }
}

function formatStatusLabel(status: string | null): string {
  if (!status) return "--";
  if (status === "returned_for_revision") return "Returned";
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function MyPayAppsPage() {
  const router = useRouter();
  const { data, isLoading, isError, error, refetch } = useSubPayApps();
  const [activeStatus, setActiveStatus] = useState<(typeof STATUSES)[number]>("All");
  const [filterProject, setFilterProject] = useState("__all__");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortCol, setSortCol] = useState<SortCol>("payAppNumber");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [commitmentId, setCommitmentId] = useState("");
  const [billingPeriodId, setBillingPeriodId] = useState("");

  const { data: projectsList } = useProjects();
  const { data: commitmentsList } = useCommitments(
    selectedProjectId ? { projectId: selectedProjectId } : undefined,
  );
  const { data: billingPeriods } = useBillingPeriods();
  const createMutation = useCreateSubPayApp();

  const openBillingPeriods = (billingPeriods ?? []).filter((bp) => bp.status === "OPEN");
  const subcontracts = (commitmentsList ?? []).filter((c) => c.commitmentType === "subcontract");

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load pay applications"}
        onRetry={() => refetch()}
      />
    );
  }

  const items = data ?? [];
  const statusFilter = statusToFilter(activeStatus);

  const uniqueProjects = Array.from(
    new Map(items.map((p) => [p.projectId, p.projectName])).entries(),
  ).sort((a, b) => a[1].localeCompare(b[1]));

  const statusCounts = STATUSES.reduce<Record<string, number>>((acc, s) => {
    if (s === "All") acc[s] = items.length;
    else {
      const f = statusToFilter(s);
      acc[s] = items.filter((p) => p.status === f).length;
    }
    return acc;
  }, {});

  const q = searchQuery.trim().toLowerCase();
  const filtered = items.filter((p) => {
    if (statusFilter && p.status !== statusFilter) return false;
    if (filterProject !== "__all__" && p.projectId !== filterProject) return false;
    if (q) {
      const haystack = [
        p.projectName,
        p.commitmentNumber,
        String(p.payAppNumber),
        formatStatusLabel(p.status),
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  function handleSort(col: SortCol) {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  }

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    switch (sortCol) {
      case "payAppNumber":
        return dir * ((a.payAppNumber ?? 0) - (b.payAppNumber ?? 0));
      case "projectName":
        return dir * (a.projectName ?? "").localeCompare(b.projectName ?? "");
      case "commitmentNumber":
        return dir * (a.commitmentNumber ?? "").localeCompare(b.commitmentNumber ?? "");
      case "billingPeriodEnd":
        return (
          dir *
          (new Date(a.billingPeriodEnd ?? "").getTime() -
            new Date(b.billingPeriodEnd ?? "").getTime())
        );
      case "currentPaymentDue":
        return dir * ((a.currentPaymentDue ?? 0) - (b.currentPaymentDue ?? 0));
      case "createdAt":
        return (
          dir * (new Date(a.createdAt ?? "").getTime() - new Date(b.createdAt ?? "").getTime())
        );
      case "status":
        return dir * (a.status ?? "").localeCompare(b.status ?? "");
      default:
        return 0;
    }
  });

  function SortIcon({ col }: { col: SortCol }) {
    if (sortCol !== col) return null;
    return sortDir === "asc" ? (
      <ChevronUp className="ml-0.5 inline h-3 w-3 opacity-70" />
    ) : (
      <ChevronDown className="ml-0.5 inline h-3 w-3 opacity-70" />
    );
  }

  function downloadCSV() {
    const headers = [
      "App #",
      "Project",
      "Commitment",
      "Billing Period",
      "Amount Due",
      "Added On",
      "Status",
    ];
    const rows = sorted.map((p) => [
      p.payAppNumber ?? "",
      p.projectName ?? "",
      p.commitmentNumber ?? "",
      formatDate(p.billingPeriodEnd),
      formatCurrency(p.currentPaymentDue),
      formatDate(p.createdAt),
      formatStatusLabel(p.status),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "my-pay-applications.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleCreate() {
    if (!commitmentId) return;
    createMutation.mutate(
      { commitmentId, billingPeriodId: billingPeriodId || undefined },
      {
        onSuccess: (result) => {
          setDialogOpen(false);
          setSelectedProjectId("");
          setCommitmentId("");
          setBillingPeriodId("");
          const id = (result as { data?: { id?: string } })?.data?.id;
          if (id) {
            router.navigate({ to: "/pay-applications/subcontract/$id", params: { id } });
          }
        },
      },
    );
  }

  const filtersActive =
    filterProject !== "__all__" || searchQuery.length > 0 || activeStatus !== "All";

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">My Pay Applications</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            All pay applications submitted by your company across all projects and commitments.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Pay App
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle>Create New Pay Application</DialogTitle>
              <DialogDescription>
                Pick the project and subcontract you're billing against. After creating, you can
                fill in the line items and submit for review.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Project</Label>
                <Select
                  value={selectedProjectId}
                  onValueChange={(v) => {
                    setSelectedProjectId(v);
                    setCommitmentId("");
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {(projectsList ?? []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subcontract</Label>
                <Select
                  value={commitmentId}
                  onValueChange={setCommitmentId}
                  disabled={!selectedProjectId || subcontracts.length === 0}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={
                        !selectedProjectId
                          ? "Select a project first"
                          : subcontracts.length === 0
                            ? "No subcontracts on this project"
                            : "Select subcontract"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {subcontracts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.commitmentNumber ? `${c.commitmentNumber} – ` : ""}
                        {c.title ?? "Untitled"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedProjectId && subcontracts.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No subcontracts on this project yet.
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label>Billing Period</Label>
                <Select value={billingPeriodId} onValueChange={setBillingPeriodId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Most recent open period (default)" />
                  </SelectTrigger>
                  <SelectContent>
                    {openBillingPeriods.map((bp) => (
                      <SelectItem key={bp.id} value={bp.id}>
                        {bp.periodStart} – {bp.periodEnd}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {openBillingPeriods.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No open billing periods. Contact your project administrator.
                  </p>
                ) : null}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!selectedProjectId || !commitmentId || createMutation.isPending}
              >
                {createMutation.isPending ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Status filter pill row */}
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => {
          const count = statusCounts[s] ?? 0;
          return (
            <Button
              key={s}
              variant={activeStatus === s ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveStatus(s)}
            >
              {s}
              <span
                className={`ml-2 rounded-full px-1.5 text-[10px] font-semibold ${
                  activeStatus === s
                    ? "bg-primary-foreground/20 text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {count}
              </span>
            </Button>
          );
        })}
      </div>

      {/* Search + filter + CSV */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by project, commitment, app #…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
          {searchQuery ? (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All projects</SelectItem>
            {uniqueProjects.map(([id, name]) => (
              <SelectItem key={id} value={id}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filtersActive ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilterProject("__all__");
              setSearchQuery("");
              setActiveStatus("All");
            }}
          >
            <X className="mr-1 h-3 w-3" />
            Clear
          </Button>
        ) : null}
        <Button variant="outline" size="sm" onClick={downloadCSV} className="ml-auto">
          <Download className="mr-1.5 h-4 w-4" />
          Download CSV
        </Button>
      </div>

      {sorted.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
            {items.length === 0 ? (
              <>
                <p>No pay applications yet.</p>
                <p className="mt-1 text-xs">
                  Click <span className="font-medium">New Pay App</span> to create your first one.
                </p>
              </>
            ) : (
              <p>No pay applications match the current filters.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("payAppNumber")}
                >
                  App # <SortIcon col="payAppNumber" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("projectName")}
                >
                  Project <SortIcon col="projectName" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("commitmentNumber")}
                >
                  Commitment <SortIcon col="commitmentNumber" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("billingPeriodEnd")}
                >
                  Billing Period <SortIcon col="billingPeriodEnd" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none text-right"
                  onClick={() => handleSort("currentPaymentDue")}
                >
                  Amount Due <SortIcon col="currentPaymentDue" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("createdAt")}
                >
                  Added On <SortIcon col="createdAt" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("status")}
                >
                  Status <SortIcon col="status" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((payApp) => (
                <TableRow
                  key={payApp.id}
                  className="cursor-pointer"
                  onClick={() =>
                    router.navigate({
                      to: "/pay-applications/subcontract/$id",
                      params: { id: payApp.id },
                    })
                  }
                >
                  <TableCell className="font-mono font-medium">
                    {payApp.payAppNumber ?? "--"}
                  </TableCell>
                  <TableCell className="text-sm">{payApp.projectName || "--"}</TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        router.navigate({
                          to: "/commitments/$id",
                          params: { id: payApp.commitmentId },
                        });
                      }}
                      className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 hover:underline"
                    >
                      {payApp.commitmentNumber || "--"}
                    </button>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(payApp.billingPeriodEnd)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(payApp.currentPaymentDue)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(payApp.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(payApp.status)}>
                      {formatStatusLabel(payApp.status)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
